import React, { useState, useEffect, useCallback } from "react";
import {
  getInterventions, createIntervention, updateIntervention, deleteIntervention, getTeam,
  setInterventionStatus, rescheduleIntervention, reassignIntervention,
  getGpsSettings, updateGpsSettings, getReportsSettings, updateReportsSettings,
  getReminderSettings, updateReminderSettings,
} from "../../api";
import { useToast } from "../../hooks/use-toast";
import { overlapText } from "../../hooks/useHolidays";
import { Plus, MapPin, QrCode, List, LayoutGrid } from "lucide-react";
import InterventionForm from "./interventions/InterventionForm";
import InterventionCard from "./interventions/InterventionCard";
import InterventionKanban from "./interventions/InterventionKanban";
import { EditModal, ReassignConfirm } from "./interventions/EditModal";
import ReplaceModal from "./interventions/ReplaceModal";
import { InterventionSettings, QrModal } from "./interventions/InterventionSettings";
import { STATUS_COLS, UNASSIGNED } from "./interventions/kanbanUtils";
import { log } from "../../lib/log";

const InterventionsPanel = ({ isMaster = true }) => {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [radius, setRadius] = useState(500);
  const [autoSend, setAutoSend] = useState(false);
  const [reminders, setReminders] = useState({ enabled: false, hour: 18 });
  const [showQr, setShowQr] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [replacing, setReplacing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingReassign, setPendingReassign] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("intv_view") || "list");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const switchView = (v) => { setView(v); localStorage.setItem("intv_view", v); };

  const load = useCallback(async () => {
    try {
      const [i, t, g, rs, rem] = await Promise.all([getInterventions(), getTeam(), getGpsSettings(), getReportsSettings(), getReminderSettings()]);
      setItems(i.data);
      setTeam((t.data.users || []).filter((u) => u.active !== false));
      setRadius(g.data.radius_m);
      setAutoSend(rs.data.auto_send);
      setReminders(rem.data);
    } catch (err) {
      log.error("Chargement des interventions :", err);
      toast({ title: "Interventions indisponibles", description: err?.response?.data?.detail || "Réessayez dans un instant.", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const settingChange = (apply, okTitle) => async (...args) => {
    try {
      await apply(...args);
      toast(okTitle(...args));
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de l'enregistrement", variant: "destructive" });
    }
  };
  const changeReminders = settingChange(
    async (next) => { setReminders(next); await updateReminderSettings(next.enabled, next.hour); },
    () => ({ title: "Rappels de planning mis à jour ✓" }),
  );
  const changeAutoSend = settingChange(
    async (val) => { setAutoSend(val); await updateReportsSettings(val); },
    (val) => ({ title: val ? "Envoi automatique activé ✓" : "Envoi automatique désactivé" }),
  );
  const changeRadius = settingChange(
    async (val) => { const r = parseInt(val, 10); setRadius(r); await updateGpsSettings(r); },
    (val) => { const r = parseInt(val, 10); return { title: "Rayon d'alerte mis à jour ✓", description: `Seuil : ${r >= 1000 ? r / 1000 + " km" : r + " m"}` }; },
  );

  const add = async (form) => {
    setLoading(true);
    try {
      const r = await createIntervention(form);
      setShowForm(false);
      load();
      toast({ title: "Intervention planifiée ✓" });
      const ov = overlapText(r.data.warnings);
      if (ov) toast({ title: "⚠️ Chevauchement d'interventions", description: ov, variant: "destructive" });
      if (r.data.warnings?.holiday) toast({ title: `⚠️ Jour férié : ${r.data.warnings.holiday}`, description: "Cette intervention est planifiée un jour férié.", variant: "destructive" });
      if (r.data.warnings?.absent?.length) toast({ title: "⚠️ Easer absent", description: r.data.warnings.absent.map((a) => `${a.nom} : ${a.type} du ${a.date_debut} au ${a.date_fin}`).join(" · "), variant: "destructive" });
      return true;
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec", variant: "destructive" });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await deleteIntervention(id);
    setEditing(null);
    load();
  };

  const showWarnings = (w) => {
    const ov = overlapText(w);
    if (ov) toast({ title: "⚠️ Chevauchement d'interventions", description: ov, variant: "destructive" });
    if (w?.holiday) toast({ title: `⚠️ Jour férié : ${w.holiday}`, variant: "destructive" });
    if (w?.absent?.length) toast({ title: "⚠️ Easer absent", description: w.absent.map((a) => `${a.nom} : ${a.type}`).join(" · "), variant: "destructive" });
  };
  const fail = (err, fallback) => toast({ title: "Erreur", description: err?.response?.data?.detail?.message || (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : fallback), variant: "destructive" });

  const save = async (it, form) => {
    setLoading(true);
    try {
      const r = await updateIntervention(it.id, form);
      setEditing(null);
      load();
      toast({ title: "Intervention mise à jour ✓" });
      showWarnings(r.data.warnings);
      return true;
    } catch (err) { fail(err, "Échec de la modification"); return false; } finally { setLoading(false); }
  };

  const changeStatus = async (it, status) => {
    try {
      await setInterventionStatus(it.id, status);
      setEditing(null);
      load();
      toast({ title: `Statut : ${STATUS_COLS.find((c) => c.key === status)?.label} ✓`, description: it.client_nom });
    } catch (err) { fail(err, "Changement de statut impossible"); }
  };

  const doReassign = async (it, login, replaceLogin, force) => {
    const toName = team.find((t) => (t.login || t.nom) === login)?.nom || login;
    try {
      const r = await reassignIntervention(it.id, { login, replace_login: replaceLogin === UNASSIGNED ? "" : replaceLogin, force });
      setPendingReassign(null);
      load();
      toast({ title: `Réaffectée à ${toName} ✓`, description: it.client_nom });
      showWarnings(r.data.warnings);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.needs_confirmation) setPendingReassign({ it, login, replaceLogin, toName, blockers: d.blockers });
      else fail(err, "Réaffectation impossible");
    }
  };

  const onKanbanMove = async (mode, id, from, to) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    if (mode === "status") return changeStatus(it, to);
    if (mode === "day") {
      if (it.status !== "planifiee") return toast({ title: "Seule une intervention planifiée peut être replanifiée", variant: "destructive" });
      try {
        const r = await rescheduleIntervention(id, `${to}T${(it.date || "").slice(11, 16) || "09:00"}`);
        load();
        toast({ title: "Intervention replanifiée ✓", description: `${it.client_nom} → ${new Date(to + "T12:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}` });
        showWarnings(r.data.warnings);
      } catch (err) { fail(err, "Replanification impossible"); }
      return;
    }
    if (to === UNASSIGNED) return toast({ title: "Déposez la carte sur un easer pour réaffecter", variant: "destructive" });
    return doReassign(it, to, from, false);
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/suivi/${token}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Lien de suivi copié ✓", description: url });
  };

  return (
    <div data-testid="interventions-panel">
      {showQr && <QrModal onClose={() => setShowQr(false)} onCopied={() => toast({ title: "Lien copié ✓" })} />}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MapPin size={20} className="text-brand" />
          <h2 className="font-heading font-bold text-xl text-neutral-900">Interventions & pointage GPS</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="qr-access-btn" onClick={() => setShowQr(true)} className="flex items-center gap-2 border border-neutral-300 text-neutral-700 hover:border-brand hover:text-brand text-sm font-semibold px-4 py-2 rounded-md">
            <QrCode size={16} /> QR accès app
          </button>
          <button data-testid="new-intervention-btn" onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Plus size={16} /> Planifier une intervention
          </button>
        </div>
      </div>
      <p className="text-sm text-neutral-500 mb-5">
        Planifiez les prestations et assignez une équipe de collaborateurs. Ils pointent le début et la fin depuis l'app mobile
        (<a href="/app" className="text-brand hover:underline">/app</a>) avec géolocalisation, photos et signature client.
      </p>

      <div className={isMaster ? "" : "hidden"}>
        <InterventionSettings radius={radius} autoSend={autoSend} reminders={reminders}
          changeRadius={changeRadius} changeAutoSend={changeAutoSend} changeReminders={changeReminders} />
      </div>

      {showForm && <InterventionForm team={team} onSubmit={add} loading={loading} />}

      <div className="flex items-center gap-1 mb-4 rounded-md border border-neutral-300 w-fit overflow-hidden" data-testid="intv-view-switch">
        <button onClick={() => switchView("list")} data-testid="intv-view-list" className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold ${view === "list" ? "bg-brand text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}><List size={14} /> Liste</button>
        <button onClick={() => switchView("kanban")} data-testid="intv-view-kanban" className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold ${view === "kanban" ? "bg-brand text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}><LayoutGrid size={14} /> Kanban</button>
      </div>

      {view === "kanban" ? (
        <InterventionKanban items={items} team={team} onMove={onKanbanMove} onOpen={setEditing} />
      ) : items.length === 0 ? (
        <div className="text-center text-neutral-400 py-12">
          <MapPin size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Aucune intervention planifiée.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => <InterventionCard key={it.id} it={it} onRemove={remove} onReload={load} onCopyLink={copyLink} onReplace={setReplacing} onEdit={setEditing} />)}
        </div>
      )}
      {editing && <EditModal it={editing} team={team} loading={loading} onSave={save} onStatus={changeStatus} onRemove={remove}
        onReplace={(it) => { setEditing(null); setReplacing(it); }} onClose={() => setEditing(null)} />}
      <ReassignConfirm pending={pendingReassign} onCancel={() => setPendingReassign(null)} onForce={(p) => doReassign(p.it, p.login, p.replaceLogin, true)} />
      {replacing && <ReplaceModal intervention={replacing} onClose={() => setReplacing(null)} onDone={load} />}
    </div>
  );
};

export default InterventionsPanel;
